import templateUrl from './edit-division-container.component.html'

/** A component that loads data and displays the page for editing a division. */
export const EditDivisionContainerComponent = {
    templateUrl,
    controller: class EditDivisionContainer {
        constructor($q, divisionService, leadsPolicyService, onboardingPolicyService,
            payrollService, payrollInvoicePolicyService, roleService, siteModuleService, utilityService) {
            'ngInject'
            this.$q = $q
            this.divisionService = divisionService
            this.leadsPolicyService = leadsPolicyService
            this.onboardingPolicyService = onboardingPolicyService
            this.payrollService = payrollService
            this.payrollInvoicePolicyService = payrollInvoicePolicyService
            this.roleService = roleService;
            this.siteModuleService = siteModuleService;
            this.utilityService = utilityService

            window.onbeforeunload = () => {
                if (this.editForm && this.editForm.$dirty) {
                    return "Unsaved changes will be lost.";
                }
                return undefined;
            }
        }

        $onInit() {
            this.utilityService.showLoading();
            this.loadInitialData()
                .catch(ex => {
                    console.error(ex);
                    alert("Initial load failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => this.utilityService.hideLoading());

        }

        onSave() {
            if (this.editForm.$invalid) {
                alert("Please correct any validation errors and try submitting again.");
            }

            this.editForm.$setSubmitted();
            this.utilityService.showLoading();

            const saveRequest = createSaveRequest(this.division);

            const saveDivision = this.division.DivisionID
                ? this.divisionService.updateDivision(this.division.DivisionID, saveRequest)
                : this.divisionService.createDivision(saveRequest);

            saveDivision
                .then(division => {
                    this.editForm.$setPristine(true);
                    this.division = mapDivisionToViewModel(division, this.rotationLengthTypes,
                        this.payrollTypes, this.payrollInvoicePolicies, this.onboardingPolicy,
                        this.modules, this.roles);
                    window.history.pushState(null, "Edit Division",
                        "Organization/EditDivision/" + this.division.DivisionID);
                })
                .catch(ex => {
                    console.error(ex);
                    alert("Saving failed: " + this.utilityService.getExceptionMessage(ex));
                })
                .then(() => this.utilityService.hideLoading());
        }

        loadInitialData() {
            const division = this.divisionID ? this.divisionService.retrieveDivision(this.divisionID)
                : this.$q.when(createNewDivision());
            const roles = this.roleService.retrieveRoles();
            const modules = this.siteModuleService.retrieveDivisionalModules();
            const countries = this.getCountries();
            const onboardingPolicy = division.then(division => {
                return division.OnboardingPolicyID
                    ? this.onboardingPolicyService.retrieveOnboardingPolicy(division.OnboardingPolicyID)
                    : null;
            });
            const rotationLengthTypes = this.getRotationLengthTypes();
            const payrollTypes = this.getPayrollTypes();
            const payrollInvoicePolicies = this.getPayrollInvoicePolicies();

            return this.$q.all([division, countries, modules, rotationLengthTypes, payrollTypes,
                payrollInvoicePolicies, onboardingPolicy, roles])
                .then(([division, countries, modules, rotationLengthTypes, payrollTypes,
                    payrollInvoicePolicies, onboardingPolicy, roles]) => {
                    this.countries = countries;
                    this.modules = modules;
                    this.rotationLengthTypes = rotationLengthTypes;
                    this.payrollTypes = payrollTypes;
                    // Display a pre-made list of policies, instead of defining the policy in the
                    // division.
                    this.payrollInvoicePolicies = payrollInvoicePolicies;
                    this.onboardingPolicy = onboardingPolicy;
                    this.roles = roles;
                    // Assign value objects based on the list data that was loaded.
                    this.division = mapDivisionToViewModel(division, this.rotationLengthTypes,
                        this.payrollTypes, this.payrollInvoicePolicies, this.onboardingPolicy,
                        this.modules, this.roles)
                });
        }

        getRotationLengthTypes() {
            return this.$q.when([{
                Text: "Day",
                Value: 1
            }, {
                Text: "Week",
                Value: 2
            }]);
        }

        getPayrollTypes() {
            return this.payrollService.retrievePayrollTypes();
        }

        getPayrollInvoicePolicies() {
            return this.payrollInvoicePolicyService.retrievePayrollInvoicePolicies();
        }

        getCountries() {
            return this.$q.when([{
                CountryID: 1,
                CountryName: "United States",
                Abbr: "USA"
            }, {
                CountryID: 2,
                CountryName: "Canada",
                Abbr: "CA"
            }]);
        }
    },
    controllerAs: "vm",
    bindings: {
        divisionID: "<?divisionId"
    }
}

function createNewDivision() {
    return {
        // A profile is a concrete implementation of a profile.
        DivisionProfiles: [],
        // These policies are required by default.
        RotationPolicy: {},
        PayrollPolicy: {
            PayPeriodLength: 7
        },
        CommissionPolicy: {},
        // Select from a list. Null represents no option chosen.
        PayrollInvoicePolicy: null,
        LeadsPolicy: null,
        Modules: [],
        Roles: []
    };
}

function mapDivisionToViewModel(division, rotationLengthTypes, payrollTypes,
    payrollInvoicePolicies, onboardingPolicy, modules, roles) {

    // Re-organize the division so that it can be more easily editable in child components.
    division.RotationPolicy.RotationLengthType = rotationLengthTypes.filter(function (type) {
        return type.Value === division.RotationPolicy.RotationLengthTypeID;
    })[0] || null;
    division.PayrollPolicy.PayrollType = payrollTypes.filter(function (type) {
        return type.PayrollTypeID === division.PayrollPolicy.PayrollTypeID;
    })[0] || null;
    // The entire payroll invoice policy is read-only an can only be selected from a list.
    division.PayrollInvoicePolicy = payrollInvoicePolicies.filter(function (policy) {
        return policy.PayrollInvoicePolicyID === division.PayrollInvoicePolicyID;
    })[0] || null;
    division.OnboardingPolicy = onboardingPolicy;

    division.DivisionProfiles.forEach(function (divisionProfile) {
        divisionProfile.RotationPolicy.RotationLengthType = rotationLengthTypes.filter(function (type) {
            return type.Value === divisionProfile.RotationPolicy.RotationLengthTypeID;
        })[0] || null;
        divisionProfile.PayrollInvoicePolicy = payrollInvoicePolicies.filter(function (policy) {
            return policy.PayrollInvoicePolicyID === divisionProfile.PayrollInvoicePolicyID;
        })[0] || null;
        divisionProfile.PayrollPolicyHistory.forEach(function (archive) {
            archive.PayrollPolicy && archive.PayrollPolicy.PayrollPolicyDepartments.forEach(function (dept) {
                if (dept.PayrollEntryPolicy) {
                    dept.PayrollEntryPolicy.PayrollType = payrollTypes.filter(function (type) {
                        return type.PayrollTypeID === dept.PayrollEntryPolicy.PayrollTypeID;
                    })[0] || null;
                }
            });
        });
    });
    division.modules = modules.map(_module => ({
        ..._module,
        enabled: division.Modules.reduce((any, m) => any || m === _module.ModuleID, false)
    }));
    division.roles = roles.sort((a, b) => {
        if (a.GlobalRole && !b.GlobalRole) return -1;
        if (!a.GlobalRole && b.GlobalRole) return 1;
        return a.Name < b.Name ? -1 : 1;
    })
        .map(role => ({
            ...role,
            enabled: !role.GlobalRole || role.IsSuperUser
                || division.Roles.reduce((any, r) => any || r === role.Id, false)
        }));

    return division;
}

/**
 * Create a save request from the given division.
 * @param {any} division The division view model.
 * @returns {any} The request to save the division.
 */
function createSaveRequest(division) {
    return {
        Name: division.Name,
        UseRSSchedule: division.UseRSSchedule,
        RotationPolicy: {
            RotationLengthTypeID: division.RotationPolicy.RotationLengthType.Value,
            RotationLength: division.RotationPolicy.RotationLength
        },
        PayrollPolicy: {
            PayrollTypeID: division.PayrollPolicy.PayrollType.PayrollTypeID,
            CommissionPieceCount: division.PayrollPolicy.CommissionPieceCount,
            // Coerce into true booleans, since these fields are required in the API controller.
            UseCommissionThreshold: !!division.PayrollPolicy.UseCommissionThreshold,
            UseGuaranteedHours: !!division.PayrollPolicy.UseGuaranteedHours,
            PayPeriodLength: division.PayrollPolicy.PayPeriodLength,
            EnablePayrollDataSync: !!division.PayrollPolicy.EnablePayrollDataSync,
            RepsRequireTime: !!division.PayrollPolicy.RepsRequireTime,
            UseExtraHours: !!division.PayrollPolicy.UseExtraHours,
            ShowTrainingPay: !!division.PayrollPolicy.ShowTrainingPay,
            ShowChargeback: !!division.PayrollPolicy.ShowChargeback
        },
        TimeEntryPolicy: division.TimeEntryPolicy,
        CommissionPolicy: {
            CommissionTierLimit: division.CommissionPolicy.CommissionTierLimit,
            UseDailyNCRThreshold: !!division.CommissionPolicy.UseDailyNCRThreshold
        },
        PayrollInvoicePolicyID: !division.PayrollInvoicePolicy ? null
            : division.PayrollInvoicePolicy.PayrollInvoicePolicyID,
        DivisionProfilesWithNextRotation: division.DivisionProfiles.map(function createProfileSaveRequest(profile) {
            return {
                DivisionProfileID: profile.DivisionProfileID,
                Name: profile.Name,
                TimeEntryPolicy: profile.TimeEntryPolicy,
                RotationPolicy: {
                    RotationLengthTypeID: profile.RotationPolicy.RotationLengthType === null ? null
                        : profile.RotationPolicy.RotationLengthType.Value,
                    RotationLength: profile.RotationPolicy.RotationLength
                },
                PayrollInvoicePolicy: !profile.PayrollInvoicePolicy ? null
                    : profile.PayrollInvoicePolicy.PayrollInvoicePolicyID,
                NextRotation: {
                    // Let the server pick the its own local timezone for these dates.
                    StartDate: kendo.toString(profile.NextRotation.StartDate, "yyyy-MM-dd"),
                    EndDate: kendo.toString(profile.NextRotation.EndDate, "yyyy-MM-dd")
                },
                ShowTip: profile.ShowTip
            };
        }),
        Modules: division.modules.reduce((modules, m) => {
            if (m.enabled) {
                modules.push(m.ModuleID)
            }
            return modules;
        }, []),
        Roles: division.roles.reduce((roles, r) => {
            if (r.enabled) {
                roles.push(r.Id);
            }
            return roles;
        }, [])
    };
}
